/**
 * The looks the back office can wear, as DATA.
 *
 * ONE EXPERIENCE, FIVE PAINTS (founder, 2026-10-07: "a theme only changes the
 * visualization, never the experience"). Every look renders the same component
 * tree — PAPER's: the sidebar, top bar, page compositions, drawers, dialogs and
 * till — and differs only in how it is painted: palette, borders, shadows,
 * radii, buttons and type. So this file lists names, never structure, and no
 * component branches on the look.
 *
 * The list is the single source of truth: the appearance page renders it, the
 * body-attribute hook validates against it, and `registry.spec.ts` checks that
 * every id here has a `<id>.css` beside it and that every `.css` in this folder
 * has an id here — so adding a sixth look is exactly three things: a short
 * skin file, a row below, and its name in the message catalogs. Nothing else
 * knows the list. See `apps/restaurant/AGENTS.md` ("Looks").
 *
 * `storefrontLayouts` is the bridge to the buyer side: a restaurant whose
 * storefront runs a skin that has a back-office twin (`settings.layout ===
 * "stack"`) is offered that look as "matches your storefront". It is a hint,
 * never an automatic switch — the look is the STAFF's working environment, and
 * a seller trying storefront designs must not have the tool they are doing it
 * in change under their hands with every click.
 */
export const DASHBOARD_THEME_IDS = [
  // The default look leads the picker (founder, 2026-10-07).
  "paper",
  "trioty",
  "homegrill",
  "stack",
  "verde",
] as const;

export type DashboardThemeId = (typeof DASHBOARD_THEME_IDS)[number];

/**
 * What a device that has never chosen wears. PAPER since 2026-10-02 (founder:
 * the redesign is the default); a device that picked a look keeps it. Specs
 * start here too (`test-support/setup.ts`).
 */
export const DEFAULT_DASHBOARD_THEME: DashboardThemeId = "paper";

export interface DashboardTheme {
  id: DashboardThemeId;
  /** Buyer `settings.layout` values this look is the back-office twin of. */
  storefrontLayouts: readonly string[];
}

export const DASHBOARD_THEMES: readonly DashboardTheme[] = [
  // A look for the restaurant itself, not the twin of a storefront skin.
  { id: "paper", storefrontLayouts: [] },
  { id: "trioty", storefrontLayouts: [] },
  { id: "homegrill", storefrontLayouts: ["homegrill"] },
  { id: "stack", storefrontLayouts: ["stack"] },
  { id: "verde", storefrontLayouts: ["verde"] },
];

export function isDashboardThemeId(value: unknown): value is DashboardThemeId {
  return (
    typeof value === "string" &&
    (DASHBOARD_THEME_IDS as readonly string[]).includes(value)
  );
}

/** The look that twins a storefront layout, or null when none does. */
export function dashboardThemeForLayout(
  layout: string | null | undefined,
): DashboardThemeId | null {
  if (!layout) return null;
  return (
    DASHBOARD_THEMES.find((theme) => theme.storefrontLayouts.includes(layout))
      ?.id ?? null
  );
}
